import { useEffect, useMemo, useState } from 'react';
import { getLastLoginData } from '@/services/fingerprint/fingerprint.service';
import { Fingerprint } from '@/services/fingerprint/fingerprint.types';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import ErrorText from '../../../../components/ErrorText';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DIMENSION_CLASSES } from '@/features/user-profile/profile-settings/constants';

const columnHelper = createColumnHelper<Fingerprint>();

const SecurityPanel = () => {
  const [loginData, setLoginData] = useState<Fingerprint[]>([]);
  const [errorText, setErrorText] = useState<string>();

  const getLoginData = async () => {
    try {
      const { data } = await getLastLoginData();
      setLoginData(data);
    } catch (_) {
      setLoginData([]);
      setErrorText('Failed to fetch login data. Please try again later.');
    }
  };

  useEffect(() => {
    (async () => {
      await getLoginData();
    })();
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        enableSorting: false,
        header: 'Login Date',
        cell: ({ getValue }) => format(new Date(getValue()), 'dd/MM/yyyy - HH:mm'),
      }),
      columnHelper.accessor('country', {
        enableSorting: false,
        header: 'Location',
        cell: ({ row }) =>
          row.original.city ? `${row.original.city}, ${row.original.country}` : row.original.country || 'Not available',
      }),

      columnHelper.accessor('ipAddress', {
        enableSorting: false,
        header: 'IP Address',
        cell: ({ getValue }) => getValue(),
      }),
    ],
    [],
  );

  const tableInstance = useReactTable<Fingerprint>({
    columns,
    data: loginData,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={DIMENSION_CLASSES}>
      <p className="mb-8 mt-6 text-sm font-normal text-ph-gray-dark">
        This table displays the last five times you successfully logged in to your account.
      </p>

      <table className="w-full">
        <thead>
          <tr>
            {tableInstance.getFlatHeaders().map((header) => (
              <th
                key={header.id}
                className={twMerge(
                  'bg-pn-violet-medium py-2 text-left text-xs font-bold text-ph-gray-dark first:rounded-l-xl first:pl-5 last:rounded-r-xl',
                )}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {tableInstance.getRowModel().rows.map((row) => {
            return (
              <tr
                key={row.id}
                className={twMerge(
                  'h-12 py-2 text-left text-sm font-semibold text-ph-gray-silver hover:bg-pn-violet-medium',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {loginData.length === 0 && !errorText && (
        <div className="flex h-96 w-full items-center justify-center text-center">
          <p className="text-ph-gray-silver">No data to display!</p>
        </div>
      )}

      {errorText && (
        <div className="flex h-96 w-full items-center justify-center text-center">
          <ErrorText>{errorText}</ErrorText>
        </div>
      )}
    </div>
  );
};

export default SecurityPanel;
